import type {
  AuthMethod,
  Confidence,
  ConnectorStandard,
  CurrentType,
  EipaSourceTimestamps,
  PaymentMethod,
  PointAvailability,
  StationType,
  UnconfirmedReason,
  VoltageClass,
  VoltageSource,
  Weekday,
} from '@ev/shared'

/**
 * All user-facing copy. Polish is the default; English sits alongside it.
 *
 * Components must not contain literal copy - they read it from here through
 * `useStrings()`. The `Record<Union, string>` shapes mean a new connector
 * standard or availability state is a compile error until both languages have
 * a label for it.
 */

export const LANGUAGES = ['pl', 'en'] as const
export type Language = (typeof LANGUAGES)[number]
export const DEFAULT_LANGUAGE: Language = 'pl'

export interface Strings {
  readonly app: {
    readonly title: string
    readonly tagline: string
    readonly languageLabel: string
    readonly sourceNote: string
    /** Skip links, the first thing a keyboard reaches on the page. */
    readonly skipToSearch: string
    readonly skipToMap: string
    readonly dismiss: string
  }
  readonly common: {
    readonly loading: string
    readonly error: string
    readonly retry: string
    readonly reset: string
    readonly close: string
    readonly all: string
    readonly none: string
    readonly unknown: string
  }
  /**
   * The state of the published data itself, as opposed to the state of any one
   * panel: a stale publish, a partial one, or one that does not match its
   * contract. Every one of these is something a visitor can act on - reload,
   * come back later, or tell somebody the deploy is broken - so none of them is
   * allowed to render as an empty map.
   */
  readonly dataHealth: {
    readonly staleTitle: string
    readonly staleBody: (age: string, when: string) => string
    readonly degradedTitle: string
    readonly degradedBody: string
    readonly partialTitle: string
    readonly partialBody: (what: string) => string
    readonly liveStatus: string
    readonly prices: string
    readonly ageHours: (hours: number) => string
    readonly ageDays: (days: number) => string
    /** Headline per failure kind. The map never says "check your connection"
        when the real problem is a publish that does not validate. */
    readonly failure: Record<'network' | 'http' | 'parse' | 'validation' | 'unknown', string>
    readonly failureHint: Record<'network' | 'http' | 'parse' | 'validation' | 'unknown', string>
    /** Names the artifact that failed, e.g. "stations.geojson". */
    readonly failureSource: (source: string) => string
    readonly technicalDetail: string
  }
  readonly search: {
    readonly label: string
    readonly placeholder: string
    readonly resultsCount: (count: number) => string
    readonly noResults: string
    /** Accessible name of the results list. */
    readonly results: string
    readonly clear: string
    /** Accessible name of the operator suggestions offered above the sites. */
    readonly operators: string
    /**
     * Accessible name of the suggestions from inside the `other` bucket - the
     * networks too small for the registry to track as brands.
     */
    readonly otherNetworks: string
    /** What choosing a suggested brand does, per current filter state. */
    readonly operatorFilterOn: string
    readonly operatorFilterOff: string
  }
  readonly filters: {
    readonly title: string
    readonly operators: string
    /** Legend of the roaming-card shortcuts above the operator list. */
    readonly chargeCards: string
    /** One line saying what pressing a card does to the operator filter. */
    readonly chargeCardHint: string
    /** How many registry brands one card covers. */
    readonly chargeCardOperators: (count: number) => string
    readonly connectors: string
    readonly currentType: string
    readonly minPower: string
    readonly minPowerUnit: string
    /** Label of the field for typing a threshold the slider has no stop for. */
    readonly minPowerExact: string
    readonly voltageClass: string
    readonly availability: string
    readonly paymentMethod: string
    readonly clearAll: string
    readonly activeCount: (count: number) => string
    /** Summary shown in place of {@link activeCount} when nothing is selected. */
    readonly noneActive: string
    /** Label of the button that opens the filter sheet on a narrow screen. */
    readonly showFilters: string
    /** Slider readout when no minimum power is set. */
    readonly anyPower: string
    /** The single availability checkbox: sites with at least one free point. */
    readonly freePointsOnly: string
    /** The single payment checkbox: contactless card accepted. */
    readonly contactlessCard: string
    /** Label for the `other` operator bucket, which the registry names in Polish. */
    readonly otherOperators: string
    /** Accessible text for a facet row's match count. */
    readonly matchingSites: (count: number) => string
    /** Heading of the nothing-matched state. */
    readonly emptyTitle: string
    /** The button beside the charge-card legend that defines a new card. */
    readonly chargeCardAdd: string
    /** The pencil on a card, which opens the editor for it. */
    readonly chargeCardEdit: (name: string) => string
    /** Offered once a built-in card has been edited or put away. */
    readonly chargeCardRestore: string
  }
  /**
   * The card editor: one dialog for defining a card and for changing one.
   *
   * A card is a wallet, not a filter dimension, so the copy talks about where a
   * card is accepted rather than about operators being ticked.
   */
  readonly cardEditor: {
    readonly createTitle: string
    readonly editTitle: string
    readonly nameLabel: string
    readonly namePlaceholder: string
    readonly nameRequired: string
    readonly nameTaken: string
    readonly operatorsRequired: string
    readonly operatorsLabel: string
    readonly operatorsSelected: (count: number) => string
    readonly selectAll: string
    readonly selectNone: string
    readonly save: string
    readonly cancel: string
    readonly remove: string
    /** Second press of the delete button, so nothing is lost to one mis-click. */
    readonly removeConfirm: string
    /** Says plainly that a card lives in this browser and nowhere else. */
    readonly localNote: string
  }
  readonly map: {
    readonly label: string
    readonly attribution: string
    readonly zoomIn: string
    readonly zoomOut: string
    readonly locate: string
    /** Title on the locate control when the browser will not give a position. */
    readonly locateUnavailable: string
    /** Credit for the charger data, shown in MapLibre's attribution control. */
    readonly dataAttribution: string
    readonly resetView: string
    readonly resetViewShort: string
    /** While the engine is downloading, before there is anything to show. */
    readonly preparing: string
    readonly loadFailed: string
    readonly announce: (visible: number, matching: number) => string
    readonly ignoredFacets: (facets: readonly string[]) => string
    readonly points: (ac: number, dc: number) => string
    readonly keyboardTitle: string
    readonly keyboardHint: string
    readonly keyboardList: string
    readonly keyboardEmpty: string
  }
  readonly detail: {
    readonly title: string
    readonly empty: string
    readonly address: string
    readonly operator: string
    readonly openingHours: string
    readonly alwaysOpen: string
    readonly accessibility: string
    readonly stations: string
    readonly points: string
    readonly connectors: string
    readonly power: string
    readonly voltage: string
    readonly cableAttached: string
    readonly cableAttachedYes: string
    readonly cableAttachedNo: string
    readonly price: string
    readonly pricePerKwh: string
    readonly pricePerMinute: string
    readonly authMethods: string
    readonly paymentMethods: string
    readonly lastUpdated: string
    readonly noPrice: string
    readonly loadFailed: string
    readonly loadFailedHint: string
    readonly notFound: string
    readonly closePanel: string
    readonly summary: (stations: number, points: number) => string
    readonly stationHeading: (index: number, total: number) => string
    readonly pointCode: string
    readonly chargingModes: string
    readonly status: string
    readonly statusAt: (when: string) => string
    readonly statusCounts: (free: number, total: number) => string
    readonly noStatus: string
    readonly distance: string
    readonly distanceAway: (formatted: string) => string
    readonly showDistance: string
    readonly locating: string
    readonly geolocationDenied: string
    readonly geolocationFailed: string
    readonly directions: string
    readonly directionsHint: string
    readonly openingHoursNone: string
    readonly connectorHeading: (index: number, total: number) => string
    readonly noConnectors: string
    readonly noPoints: string
    /**
     * The charge-card shortcuts at the top of the panel, shown only for cards
     * the driver is holding that pay at this operator.
     */
    readonly chargeCards: string
    readonly chargeCardStart: string
    readonly chargeCardOpen: (card: string) => string
  }
  /**
   * Sites the pipeline could not confirm against any other source, and the
   * yes/no question the panel puts to the reader about them.
   *
   * The copy is per reason and deliberately descriptive. "We are not sure this
   * station is there" would be one sentence for all three and it would be wrong
   * for the commonest: several stations listed at one address means we cannot
   * tell *which* of them a record is, not that the charger is missing. Saying
   * what we actually know is also what makes the question answerable - a reader
   * who can see three stations in the car park knows how to answer it, and a
   * reader told the register does not list this one knows their own eyes are the
   * only thing that can.
   */
  readonly unconfirmed: {
    /**
     * The hover card's label, beside the pin's warning mark. Per reason, and a
     * few words at most: the mark is a glyph and says nothing on its own, and
     * the card is read in passing over a moving map.
     */
    readonly badge: Record<UnconfirmedReason, string>
    readonly heading: string
    /** What we could not settle, at length, for the panel. */
    readonly reason: Record<UnconfirmedReason, string>
    /** The contested wording with the count filled in, when we know it. */
    readonly addressContested: (listings: number) => string
    /** The question under the explanation. Answered yes or no. */
    readonly question: string
    readonly yes: string
    readonly no: string
    readonly score: (score: number) => string
    /** How much more weight of confirmation the warning needs before it goes. */
    readonly toGo: (remaining: number) => string
    /** And on how many further days, which weight alone cannot satisfy. */
    readonly daysToGo: (days: number) => string
    readonly confirmed: string
    readonly reportedGone: string
    readonly thanks: string
    readonly failed: string
    /** Refused by the hourly vote limit, which is not a failure to retry. */
    readonly rateLimited: string
    readonly voting: string
  }
  readonly voltage: {
    readonly heading: string
    readonly declared: string
    readonly derived: string
    readonly unknown: string
    readonly declaredHint: string
    readonly derivedHint: string
    readonly unknownHint: string
    readonly unknownExplanation: string
    readonly methodology: string
    readonly methodologyTitle: string
    readonly methodologyOpen: string
    readonly methodologyMissing: string
    readonly methodologyMissingPath: string
    readonly nominal: string
    readonly range: string
    readonly atLeast: (formatted: string) => string
    readonly atMost: (formatted: string) => string
    readonly voltUnit: string
    readonly ampUnit: string
    readonly amps: string
    readonly phases: string
    readonly phasesValue: (count: number) => string
    readonly confidenceLabel: string
    readonly source: Record<VoltageSource, string>
    readonly sourceHint: Record<VoltageSource, string>
    readonly confidence: Record<Confidence, string>
    /** Heading of the block that prints every figure published for a connector. */
    readonly conflictTitle: string
    /** Marks the reading the badge above is showing. */
    readonly conflictShown: string
    /** Why one of them is on the badge, in one sentence. */
    readonly conflictHint: string
    /** EIPA, named as the side of a disagreement rather than as a source card. */
    readonly registrySource: string
  }
  readonly legend: {
    readonly title: string
    readonly expand: string
    readonly collapse: string
    readonly operators: string
    readonly power: string
    readonly voltageClasses: string
    readonly provenance: string
    readonly powerNote: string
    readonly voltageNote: string
    /** What the outer ring of a map dot encodes. */
    readonly brandRing: string
    readonly cluster: string
  }
  readonly powerBand: {
    readonly slow: string
    readonly fast: string
    readonly rapid: string
    readonly ultra: string
  }
  /** The same four bands, short enough for a swatch row on a phone. */
  readonly powerBandShort: {
    readonly slow: string
    readonly fast: string
    readonly rapid: string
    readonly ultra: string
  }
  readonly footer: {
    readonly data: string
    readonly basemap: string
    readonly about: string
    readonly methodology: string
    readonly updated: (when: string) => string
  }
  readonly about: {
    readonly title: string
    readonly open: string
    readonly intro: string
    readonly sourcesHeading: string
    readonly licence: string
    readonly lastUpdated: string
    readonly perFileTimestamps: string
    readonly eipaName: string
    readonly eipaDescription: string
    readonly eipaLicence: string
    readonly ocmName: string
    readonly ocmDescription: string
    readonly ocmLicence: string
    /** The community datasets behind the electrics, credited without being named. */
    readonly otherName: string
    readonly otherDescription: string
    readonly otherLicence: string
    readonly openFreeMapName: string
    readonly openFreeMapDescription: string
    readonly openFreeMapLicence: string
    readonly maplibreName: string
    readonly maplibreDescription: string
    readonly maplibreLicence: string
    readonly termsHeading: string
    readonly termsIntro: string
    readonly terms: readonly string[]
    readonly termsCompliance: string
    readonly termsAccess: string
    readonly disclaimer: string
    readonly qualityHeading: string
    readonly qualityIntro: string
    readonly countsHeading: string
    readonly voltageHeading: string
    readonly matchHeading: string
    readonly availabilityHeading: string
    readonly sites: string
    readonly stations: string
    readonly points: string
    readonly connectors: string
    readonly operatorCount: string
    readonly declared: string
    readonly derived: string
    readonly unknownVoltage: string
    readonly ocmCandidates: string
    readonly ocmMatched: string
    readonly ocmUnmatched: string
    readonly ocmMatchRate: string
    readonly generated: string
    readonly pipelineVersion: string
    readonly loadFailed: string
    readonly share: (part: number, total: number) => string
  }
  readonly eipaSource: Record<keyof EipaSourceTimestamps, string>
  readonly weekday: Record<Weekday, string>
  readonly authMethod: Record<AuthMethod, string>
  readonly stationType: Record<StationType, string>
  readonly currentType: Record<CurrentType, string>
  readonly availability: Record<PointAvailability, string>
  readonly voltageClass: Record<VoltageClass, string>
  readonly connector: Record<ConnectorStandard, string>
  readonly paymentMethod: Record<PaymentMethod, string>
}

const pl: Strings = {
  app: {
    title: 'Ładowarki EV w Polsce',
    tagline: 'Publiczna mapa punktów ładowania samochodów elektrycznych',
    languageLabel: 'Język',
    sourceNote: 'Dane: EIPA (UDT) oraz Open Charge Map',
    skipToSearch: 'Przejdź do wyszukiwania',
    skipToMap: 'Przejdź do mapy',
    dismiss: 'Ukryj',
  },
  common: {
    loading: 'Wczytywanie…',
    error: 'Wystąpił błąd',
    retry: 'Spróbuj ponownie',
    reset: 'Wyczyść',
    close: 'Zamknij',
    all: 'Wszystkie',
    none: 'Brak',
    unknown: 'Nieznane',
  },
  dataHealth: {
    staleTitle: 'Dane mogą być nieaktualne',
    staleBody: (age, when) => `Ostatnia publikacja ${age} temu, ${when}.`,
    degradedTitle: 'Publikacja niepełna',
    degradedBody:
      'Ta publikacja powstała bez danych Open Charge Map, więc każde napięcie jest wyliczone, a nie zadeklarowane przez operatora.',
    partialTitle: 'Część danych jest niedostępna',
    partialBody: (what) => `Nie udało się wczytać: ${what}. Reszta mapy działa normalnie.`,
    liveStatus: 'dostępność punktów na żywo',
    prices: 'ceny',
    ageHours: (hours) => (hours === 1 ? 'godzinę' : `${hours} godz.`),
    ageDays: (days) => (days === 1 ? 'dzień' : `${days} dni`),
    failure: {
      network: 'Brak połączenia z serwerem danych',
      http: 'Serwer danych zwrócił błąd',
      parse: 'Plik danych jest uszkodzony',
      validation: 'Opublikowane dane nie pasują do kontraktu',
      unknown: 'Nie udało się wczytać danych',
    },
    failureHint: {
      network: 'Sprawdź połączenie i spróbuj ponownie.',
      http: 'Publikacja może trwać. Spróbuj ponownie za chwilę.',
      parse: 'To błąd po stronie publikacji, nie po Twojej.',
      validation:
        'Plik dotarł, ale ma inny kształt, niż zakłada aplikacja. To błąd publikacji - dane nie zostaną pokazane, bo mogłyby być nieprawdziwe.',
      unknown: 'Spróbuj ponownie.',
    },
    failureSource: (source) => `Plik: ${source}`,
    technicalDetail: 'Szczegóły techniczne',
  },
  search: {
    label: 'Szukaj',
    placeholder: 'Miasto, nazwa lub operator',
    resultsCount: (count) => `Znaleziono ${count} lokalizacji`,
    noResults: 'Brak lokalizacji spełniających kryteria',
    results: 'Wyniki wyszukiwania',
    clear: 'Wyczyść wyszukiwanie',
    operators: 'Operatorzy',
    otherNetworks: 'Sieci spoza listy operatorów',
    operatorFilterOn: 'Filtr włączony',
    operatorFilterOff: 'Filtruj po operatorze',
  },
  filters: {
    title: 'Filtry',
    operators: 'Operator',
    chargeCards: 'Karty ładowania',
    chargeCardHint: 'Zaznacza operatorów, u których karta działa.',
    chargeCardOperators: (count) => `${count} operatorów`,
    connectors: 'Złącze',
    currentType: 'Rodzaj prądu',
    minPower: 'Moc minimalna',
    minPowerUnit: 'kW',
    minPowerExact: 'Moc minimalna w kW',
    voltageClass: 'Napięcie',
    availability: 'Dostępność',
    paymentMethod: 'Płatność',
    clearAll: 'Wyczyść filtry',
    activeCount: (count) => `Aktywne filtry: ${count}`,
    noneActive: 'Bez filtrów',
    showFilters: 'Filtry i wyszukiwanie',
    anyPower: 'Dowolna',
    freePointsOnly: 'Tylko z wolnym punktem',
    contactlessCard: 'Karta zbliżeniowa',
    otherOperators: 'Inni operatorzy',
    matchingSites: (count) => `${count} pasujących lokalizacji`,
    emptyTitle: 'Nic nie pasuje',
    chargeCardAdd: 'Dodaj kartę',
    chargeCardEdit: (name) => `Edytuj kartę ${name}`,
    chargeCardRestore: 'Przywróć domyślne karty',
  },
  cardEditor: {
    createTitle: 'Nowa karta ładowania',
    editTitle: 'Edycja karty ładowania',
    nameLabel: 'Nazwa karty',
    namePlaceholder: 'np. eVolt',
    nameRequired: 'Podaj nazwę karty.',
    nameTaken: 'Karta o tej nazwie już istnieje.',
    operatorsRequired: 'Wybierz co najmniej jednego operatora.',
    operatorsLabel: 'Operatorzy, u których karta działa',
    operatorsSelected: (count) => `Wybrano: ${count}`,
    selectAll: 'Zaznacz wszystkich',
    selectNone: 'Odznacz wszystkich',
    save: 'Zapisz kartę',
    cancel: 'Anuluj',
    remove: 'Usuń kartę',
    removeConfirm: 'Potwierdź usunięcie',
    localNote: 'Karty zapisujemy tylko w tej przeglądarce.',
  },
  map: {
    label: 'Mapa ładowarek',
    attribution: 'Dane mapy © OpenStreetMap',
    zoomIn: 'Przybliż',
    zoomOut: 'Oddal',
    locate: 'Moja lokalizacja',
    locateUnavailable: 'Lokalizacja niedostępna',
    dataAttribution: 'Dane ładowarek: EIPA (UDT), Open Charge Map, inne źródła',
    resetView: 'Pokaż całą Polskę',
    resetViewShort: 'PL',
    preparing: 'Przygotowywanie mapy…',
    loadFailed: 'Nie udało się wczytać lokalizacji',
    announce: (visible, matching) =>
      `${visible} z ${matching} pasujących lokalizacji w widocznym obszarze`,
    ignoredFacets: (facets) => `Filtry pominięte przez mapę: ${facets.join(', ')}`,
    points: (ac, dc) => `${ac} AC, ${dc} DC`,
    keyboardTitle: 'Nawigacja klawiaturą',
    keyboardHint: 'Strzałki przewijają listę, Enter wybiera lokalizację.',
    keyboardList: 'Lokalizacje w widocznym obszarze',
    keyboardEmpty: 'Brak lokalizacji w widocznym obszarze',
  },
  detail: {
    title: 'Szczegóły lokalizacji',
    empty: 'Wybierz punkt na mapie, aby zobaczyć szczegóły',
    address: 'Adres',
    operator: 'Operator',
    openingHours: 'Godziny otwarcia',
    alwaysOpen: 'Całodobowo',
    accessibility: 'Dojazd i dostęp',
    stations: 'Stacje',
    points: 'Punkty ładowania',
    connectors: 'Złącza',
    power: 'Moc',
    voltage: 'Napięcie',
    cableAttached: 'Kabel',
    cableAttachedYes: 'Kabel na stałe',
    cableAttachedNo: 'Gniazdo, własny kabel',
    price: 'Cena',
    pricePerKwh: 'PLN / kWh',
    pricePerMinute: 'PLN / min',
    authMethods: 'Uwierzytelnianie',
    paymentMethods: 'Metody płatności',
    lastUpdated: 'Aktualizacja',
    noPrice: 'Operator nie podał ceny',
    loadFailed: 'Nie udało się wczytać szczegółów',
    loadFailedHint: 'Sprawdź połączenie i spróbuj ponownie.',
    notFound: 'Nie znaleziono tej lokalizacji',
    closePanel: 'Zamknij szczegóły',
    summary: (stations, points) =>
      `${stations} ${stations === 1 ? 'stacja' : 'stacje/stacji'} · ${points} ${points === 1 ? 'punkt' : 'punktów'}`,
    stationHeading: (index, total) => `Stacja ${index} z ${total}`,
    pointCode: 'Kod punktu',
    chargingModes: 'Tryb ładowania',
    status: 'Status',
    statusAt: (when) => `Stan na ${when}`,
    statusCounts: (free, total) => `${free} z ${total} wolnych`,
    noStatus: 'Operator nie przesyła statusu',
    distance: 'Odległość',
    distanceAway: (formatted) => `${formatted} od Ciebie`,
    showDistance: 'Pokaż odległość',
    locating: 'Ustalanie położenia…',
    geolocationDenied: 'Dostęp do lokalizacji odrzucony',
    geolocationFailed: 'Nie udało się ustalić położenia',
    directions: 'Wyznacz trasę',
    directionsHint: 'Otwiera nawigację w nowej karcie',
    openingHoursNone: 'Operator nie podał godzin otwarcia',
    connectorHeading: (index, total) => `Złącze ${index} z ${total}`,
    noConnectors: 'Brak złączy w rejestrze',
    noPoints: 'Brak punktów ładowania w rejestrze',
    chargeCards: 'Zapłacisz tu swoją kartą',
    chargeCardStart: 'Rozpocznij w Telegramie',
    chargeCardOpen: (card) => `Otwórz ${card} w Telegramie`,
  },
  unconfirmed: {
    badge: {
      'address-contested': 'Kilka stacji pod adresem',
      'listing-retired': 'Zgłoszony brak stacji',
      unlisted: 'Brak w rejestrze',
    },
    heading: 'Nie udało się potwierdzić tej stacji',
    reason: {
      'address-contested':
        'Pod tym adresem wymieniono kilka różnych stacji, więc nie mogliśmy ustalić, która z nich to ta.',
      'listing-retired': 'Ktoś zgłosił, że stacji wymienionej pod tym adresem już tam nie ma.',
      // Named as "inne źródło", the same way the voltage badge names it: the
      // attribution this site publishes credits that source without naming it.
      unlisted:
        'Tej stacji nie ma w rejestrze EIPA - wiemy o niej tylko z innego źródła i nic jej nie potwierdza.',
    },
    addressContested: (listings) =>
      `Pod tym adresem wymieniono ${listings} różne stacje, więc nie mogliśmy ustalić, która z nich to ta.`,
    question: 'Czy ta ładowarka tu jest?',
    yes: 'Tak, jest tutaj',
    no: 'Nie, nie ma jej',
    score: (score) => `Potwierdzenia: ${score}`,
    // "Brakuje", not "jeszcze N i zniknie": the count is one of two conditions
    // and satisfying it need not settle anything, so the sentence says what is
    // missing rather than what would happen. It also sidesteps Polish plurals -
    // after "brakuje" the genitive is 'potwierdzenia' at one and 'potwierdzeń'
    // at every other count, which is two forms rather than three.
    toGo: (remaining) =>
      remaining === 1
        ? 'Brakuje jeszcze 1 potwierdzenia.'
        : `Brakuje jeszcze ${remaining} potwierdzeń.`,
    daysToGo: (days) =>
      days === 1
        ? 'Potrzebne są odpowiedzi jeszcze z 1 kolejnego dnia.'
        : `Potrzebne są odpowiedzi jeszcze z ${days} kolejnych dni.`,
    confirmed: 'Potwierdzona przez kierowców.',
    reportedGone: 'Kierowcy zgłaszają, że tej ładowarki już nie ma.',
    thanks: 'Dziękujemy - Twoja odpowiedź została zapisana.',
    failed: 'Nie udało się zapisać odpowiedzi. Spróbuj ponownie.',
    rateLimited: 'Z tej przeglądarki zapisano ostatnio wiele odpowiedzi. Spróbuj za godzinę.',
    voting: 'Zapisywanie…',
  },
  voltage: {
    heading: 'Napięcie',
    declared: 'Zadeklarowane',
    derived: 'Wyliczone',
    unknown: 'Nieznane',
    declaredHint: 'Wartość opublikowana przez zewnętrzną bazę dla tego złącza, nie wyliczona.',
    derivedHint:
      'Wartość wyliczona z trybu ładowania i standardu złącza. Kliknij, aby poznać metodę.',
    unknownHint: 'Żadne ze źródeł nie podaje napięcia dla tego złącza.',
    unknownExplanation:
      'EIPA publikuje wyłącznie moc w kW. Gdy Open Charge Map nie ma dopasowanego rekordu, a tryb ładowania i standard złącza nie wystarczają, napięcie pozostaje nieznane. Nie podstawiamy w to miejsce prawdopodobnej liczby.',
    methodology: 'Metodologia',
    methodologyTitle: 'Skąd bierze się napięcie',
    methodologyOpen: 'Jak wyliczamy napięcie',
    methodologyMissing: 'Opis metodologii nie został jeszcze dodany do repozytorium.',
    methodologyMissingPath: 'Oczekiwany plik: docs/voltage.md',
    nominal: 'Napięcie znamionowe',
    range: 'Zakres napięcia',
    atLeast: (formatted) => `od ${formatted}`,
    atMost: (formatted) => `do ${formatted}`,
    voltUnit: 'V',
    ampUnit: 'A',
    amps: 'Prąd',
    phases: 'Fazy',
    phasesValue: (count) => (count === 1 ? '1-fazowe' : `${count}-fazowe`),
    confidenceLabel: 'Pewność',
    source: {
      'ge-declared': 'Inne źródło',
      'ocm-declared': 'Open Charge Map',
      'derived-mode': 'Tryb ładowania',
      'derived-connector': 'Standard złącza',
    },
    sourceHint: {
      'ge-declared':
        'Inne źródło opublikowało napięcie i prąd dla tego złącza. Pewność wysoka oznacza dopasowanie po identyfikatorze EVSE, średnia - po adresie stacji.',
      'ocm-declared': 'Open Charge Map opublikował napięcie i prąd dla dopasowanego złącza.',
      'derived-mode': 'Wyliczone z trybu ładowania IEC 61851 zadeklarowanego dla punktu.',
      'derived-connector':
        'Wyliczone wyłącznie ze standardu złącza - najsłabsza przesłanka. Górna granica napięcia DC pochodzi z obserwacji sprzętu tego typu w Polsce, jeśli takie są dostępne.',
    },
    confidence: {
      high: 'wysoka',
      medium: 'średnia',
      low: 'niska',
    },
    conflictTitle: 'Źródła podają różne wartości',
    conflictShown: 'wartość pokazana',
    conflictHint:
      'Pokazujemy tę wartość, która jest najmocniej powiązana z tym konkretnym złączem - nie tę, którą uznajemy za poprawną. Pewność mówi o sile powiązania, a nie o trafności liczby.',
    registrySource: 'EIPA (rejestr)',
  },
  legend: {
    title: 'Legenda',
    expand: 'Rozwiń legendę',
    collapse: 'Zwiń legendę',
    operators: 'Operatorzy',
    power: 'Moc złącza',
    voltageClasses: 'Klasy napięcia',
    provenance: 'Pochodzenie napięcia',
    powerNote: 'Kolor punktu na mapie odpowiada najwyższej mocy złącza w lokalizacji.',
    voltageNote:
      'Klasy napięcia służą wyłącznie do filtrowania i opisują możliwości ładowarki, a nie architekturę akumulatora auta. Jedna ładowarka CCS2 zwykle obejmuje obie klasy DC. Rzeczywisty zakres napięcia znajdziesz przy każdym złączu.',
    brandRing: 'Obwódka punktu oznacza operatora.',
    cluster:
      'Liczba w kółku to liczba lokalizacji, a jej kolor to pasmo mocy, w którym mieści się ich większość. Przybliż, aby rozwinąć grupę.',
  },
  powerBand: {
    slow: 'do 11 kW',
    fast: '11-50 kW',
    rapid: '50-150 kW',
    ultra: 'powyżej 150 kW',
  },
  powerBandShort: {
    slow: '< 11 kW',
    fast: '11-50',
    rapid: '50-150',
    ultra: '> 150',
  },
  footer: {
    data: 'Dane',
    basemap: 'Mapa podkładowa',
    about: 'O projekcie',
    methodology: 'Metodologia napięcia',
    updated: (when) => `dane z ${when}`,
  },
  about: {
    title: 'O projekcie i licencje',
    open: 'O projekcie',
    intro:
      'Mapa łączy oficjalny rejestr EIPA prowadzony przez Urząd Dozoru Technicznego z danymi elektrycznymi z Open Charge Map. Poniżej: skąd pochodzą dane, na jakich warunkach są tu prezentowane i jak wygląda ich jakość w bieżącej publikacji.',
    sourcesHeading: 'Źródła i licencje',
    licence: 'Licencja',
    lastUpdated: 'Ostatnia aktualizacja',
    perFileTimestamps: 'Znaczniki czasu poszczególnych plików EIPA',
    eipaName: 'EIPA / Urząd Dozoru Technicznego',
    eipaDescription:
      'Ewidencja Infrastruktury Paliw Alternatywnych - oficjalny rejestr operatorów, stacji i punktów ładowania w Polsce.',
    eipaLicence: 'Informacja sektora publicznego, udostępniana bezpłatnie na warunkach UDT',
    ocmName: 'Open Charge Map',
    ocmDescription:
      'Napięcie i natężenie prądu dla dopasowanych złączy - jedyne dane elektryczne, których EIPA nie publikuje. Wkład społeczności Open Charge Map.',
    ocmLicence: 'CC BY 4.0; dane dla Polski udostępniane na licencji ODbL',
    otherName: 'Inne źródła',
    otherDescription:
      'Napięcie i natężenie prądu dla większości polskich złączy, wraz z górnymi granicami napięcia DC dla sprzętu, który faktycznie tu stoi. Dane społecznościowe; migawka odświeżana ręcznie co kilka miesięcy.',
    otherLicence: 'Dane społecznościowe - warunki po stronie ich wydawcy',
    openFreeMapName: 'OpenFreeMap i OpenStreetMap',
    openFreeMapDescription:
      'Kafle mapy podkładowej serwowane przez OpenFreeMap, zbudowane z danych OpenStreetMap tworzonych przez społeczność.',
    openFreeMapLicence: 'ODbL - © współtwórcy OpenStreetMap',
    maplibreName: 'MapLibre GL JS',
    maplibreDescription: 'Silnik renderujący mapę w przeglądarce.',
    maplibreLicence: 'BSD-3-Clause',
    termsHeading: 'Warunki ponownego wykorzystywania danych EIPA',
    termsIntro:
      'Urząd Dozoru Technicznego publikuje warunki ponownego wykorzystywania informacji sektora publicznego w swoim Biuletynie Informacji Publicznej. Redystrybucja danych rejestru nie jest zakazana, ale jest obwarowana warunkami, które ta strona spełnia:',
    terms: [
      'Informacja zawiera wzmiankę o źródle jej pozyskania poprzez podanie pełnej nazwy Urzędu Dozoru Technicznego.',
      'Podana jest data wytworzenia lub pozyskania informacji.',
      'Zamieszczona jest adnotacja, że uzyskana informacja została przetworzona w procesie ponownego wykorzystywania.',
      'Fragmenty pozyskanej treści prezentowane są w formie cytatu, z przypisem informującym o źródle pochodzenia.',
      'Zamieszczona jest informacja, że Urząd Dozoru Technicznego nie ponosi odpowiedzialności za treść informacji powstałej w wyniku przetworzenia.',
    ],
    termsCompliance:
      'Adnotacja o przetworzeniu: dane EIPA prezentowane na tej mapie zostały przetworzone w procesie ponownego wykorzystywania. Rekordy rejestru są dopasowywane do punktów Open Charge Map, a napięcie złączy jest wyliczane, gdy żadne źródło go nie deklaruje.',
    termsAccess:
      'Dostęp do danych EIPA wymaga rejestracji i jest bezpłatny, z godzinowym limitem pobrań. Instrukcja obsługi rejestru wprost dopuszcza prezentowanie pobranych danych na własnej stronie internetowej.',
    disclaimer:
      'Urząd Dozoru Technicznego nie ponosi odpowiedzialności za aktualność danych przesyłanych przez operatorów infrastruktury do rejestru ani za treść informacji powstałej w wyniku ich przetworzenia.',
    qualityHeading: 'Jakość danych',
    qualityIntro:
      'Liczby poniżej pochodzą z pliku meta.json bieżącej publikacji - nie są wpisane na sztywno.',
    countsHeading: 'Zakres publikacji',
    voltageHeading: 'Pokrycie danymi o napięciu',
    matchHeading: 'Dopasowanie do Open Charge Map',
    availabilityHeading: 'Dostępność punktów',
    sites: 'Lokalizacje',
    stations: 'Stacje',
    points: 'Punkty ładowania',
    connectors: 'Złącza',
    operatorCount: 'Operatorzy',
    declared: 'Zadeklarowane przez Open Charge Map',
    derived: 'Wyliczone',
    unknownVoltage: 'Nieznane',
    ocmCandidates: 'Lokalizacje EIPA brane pod uwagę',
    ocmMatched: 'Dopasowane do rekordu Open Charge Map',
    ocmUnmatched: 'Bez dopasowania',
    ocmMatchRate: 'Skuteczność dopasowania',
    generated: 'Publikacja wygenerowana',
    pipelineVersion: 'Wersja potoku',
    loadFailed: 'Nie udało się wczytać meta.json',
    share: (part, total) => (total === 0 ? '-' : `${Math.round((part / total) * 100)}%`),
  },
  eipaSource: {
    dictionary: 'Słowniki',
    operator: 'Operatorzy',
    pool: 'Lokalizacje',
    station: 'Stacje',
    point: 'Punkty ładowania',
    dynamic: 'Dane dynamiczne',
  },
  weekday: {
    1: 'Poniedziałek',
    2: 'Wtorek',
    3: 'Środa',
    4: 'Czwartek',
    5: 'Piątek',
    6: 'Sobota',
    7: 'Niedziela',
  },
  authMethod: {
    unrestricted: 'Bez ograniczeń',
    'no-access': 'Brak dostępu',
    'rfid-mifare-classic': 'RFID Mifare Classic',
    'rfid-mifare-desfire': 'RFID Mifare DESFire',
    'rfid-calypso': 'RFID Calypso',
    pinpad: 'Klawiatura PIN',
    app: 'Aplikacja mobilna',
    'phone-rfid': 'Telefon z RFID',
    'iso15118-plc': 'ISO 15118 (PLC)',
    'iso15118-wireless': 'ISO 15118 (bezprzewodowo)',
    'phone-voice': 'Połączenie telefoniczne',
    'phone-sms': 'SMS',
    'prepaid-card': 'Karta przedpłacona',
    other: 'Inne',
  },
  stationType: {
    electric: 'Elektryczna',
    gas: 'CNG / LNG',
    hydrogen: 'Wodór',
  },
  currentType: {
    AC: 'Prąd przemienny (AC)',
    DC: 'Prąd stały (DC)',
    unknown: 'Nieznany',
  },
  availability: {
    free: 'Wolny',
    occupied: 'Zajęty',
    unavailable: 'Niedostępny',
    unknown: 'Brak danych',
  },
  voltageClass: {
    'ac-230': 'AC 230 V',
    'ac-400': 'AC 400 V',
    'dc-400': 'DC do 500 V',
    'dc-800': 'DC powyżej 500 V',
    unknown: 'Nieznane',
  },
  connector: {
    DOMESTIC: 'Gniazdo domowe',
    IEC_60309_1P: 'IEC 60309 1-fazowe',
    IEC_60309_3P: 'IEC 60309 3-fazowe',
    IEC_60309_DC: 'IEC 60309 DC',
    TYPE1: 'Typ 1 (J1772)',
    TYPE2: 'Typ 2 (Mennekes)',
    TYPE3A: 'Typ 3A (SCAME)',
    TYPE3C: 'Typ 3C',
    CCS1: 'CCS Combo 1',
    CCS2: 'CCS Combo 2',
    CHADEMO: 'CHAdeMO',
    TESLA: 'Tesla',
    GBT_AC: 'GB/T AC',
    GBT_DC: 'GB/T DC',
    INDUCTIVE_LARGE: 'Indukcyjne (duże)',
    INDUCTIVE_SMALL: 'Indukcyjne (małe)',
    AVCON: 'Avcon',
    MARECHAL: 'Marechal',
    BETTER_PLACE: 'Better Place',
    UNKNOWN: 'Nieznane złącze',
  },
  paymentMethod: {
    unspecified: 'Nieokreślone',
    free: 'Bezpłatne',
    'operator-contract': 'Umowa z operatorem',
    'payment-card': 'Karta płatnicza',
    cash: 'Gotówka',
    'prepaid-card': 'Karta przedpłacona',
    'fleet-card': 'Karta flotowa',
    'bank-transfer': 'Przelew',
    'online-payment': 'Płatność internetowa',
    other: 'Inne',
  },
}

const en: Strings = {
  app: {
    title: 'EV chargers in Poland',
    tagline: 'A public map of electric vehicle charging points',
    languageLabel: 'Language',
    sourceNote: 'Data: EIPA (UDT) and Open Charge Map',
    skipToSearch: 'Skip to search',
    skipToMap: 'Skip to the map',
    dismiss: 'Dismiss',
  },
  common: {
    loading: 'Loading…',
    error: 'Something went wrong',
    retry: 'Try again',
    reset: 'Clear',
    close: 'Close',
    all: 'All',
    none: 'None',
    unknown: 'Unknown',
  },
  dataHealth: {
    staleTitle: 'This data may be out of date',
    staleBody: (age, when) => `Last published ${age} ago, on ${when}.`,
    degradedTitle: 'Incomplete publish',
    degradedBody:
      'This publish was produced without Open Charge Map, so every voltage on it is derived rather than declared by an operator.',
    partialTitle: 'Some data is unavailable',
    partialBody: (what) => `Could not load ${what}. The rest of the map works normally.`,
    liveStatus: 'live point availability',
    prices: 'prices',
    ageHours: (hours) => (hours === 1 ? 'an hour' : `${hours} hours`),
    ageDays: (days) => (days === 1 ? 'a day' : `${days} days`),
    failure: {
      network: 'Could not reach the data server',
      http: 'The data server returned an error',
      parse: 'The data file is corrupt',
      validation: 'The published data does not match its contract',
      unknown: 'Could not load the data',
    },
    failureHint: {
      network: 'Check your connection and try again.',
      http: 'A publish may be in progress. Try again in a moment.',
      parse: 'This is a fault in the publish, not at your end.',
      validation:
        'The file arrived but has a different shape from the one this app expects. That is a publishing fault - the data is withheld rather than shown, because it could be wrong.',
      unknown: 'Try again.',
    },
    failureSource: (source) => `File: ${source}`,
    technicalDetail: 'Technical detail',
  },
  search: {
    label: 'Search',
    placeholder: 'City, name or operator',
    resultsCount: (count) => `${count} locations found`,
    noResults: 'No locations match these filters',
    results: 'Search results',
    clear: 'Clear search',
    operators: 'Operators',
    otherNetworks: 'Networks outside the operator list',
    operatorFilterOn: 'Filter on',
    operatorFilterOff: 'Filter by operator',
  },
  filters: {
    title: 'Filters',
    operators: 'Operator',
    chargeCards: 'Charge cards',
    chargeCardHint: 'Ticks the operators the card works at.',
    chargeCardOperators: (count) => `${count} operators`,
    connectors: 'Connector',
    currentType: 'Current type',
    minPower: 'Minimum power',
    minPowerUnit: 'kW',
    minPowerExact: 'Minimum power in kW',
    voltageClass: 'Voltage',
    availability: 'Availability',
    paymentMethod: 'Payment',
    clearAll: 'Clear filters',
    activeCount: (count) => `${count} active filters`,
    noneActive: 'No filters',
    showFilters: 'Filters and search',
    anyPower: 'Any',
    freePointsOnly: 'Only sites with a free point',
    contactlessCard: 'Contactless card accepted',
    otherOperators: 'Other operators',
    matchingSites: (count) => `${count} matching locations`,
    emptyTitle: 'Nothing matches',
    chargeCardAdd: 'Add a card',
    chargeCardEdit: (name) => `Edit the ${name} card`,
    chargeCardRestore: 'Restore the default cards',
  },
  cardEditor: {
    createTitle: 'New charge card',
    editTitle: 'Edit charge card',
    nameLabel: 'Card name',
    namePlaceholder: 'e.g. eVolt',
    nameRequired: 'Give the card a name.',
    nameTaken: 'A card with that name already exists.',
    operatorsRequired: 'Pick at least one operator.',
    operatorsLabel: 'Operators the card is accepted at',
    operatorsSelected: (count) => `${count} selected`,
    selectAll: 'Select all',
    selectNone: 'Clear selection',
    save: 'Save card',
    cancel: 'Cancel',
    remove: 'Delete card',
    removeConfirm: 'Confirm deletion',
    localNote: 'Cards are stored in this browser only.',
  },
  map: {
    label: 'Charger map',
    attribution: 'Map data © OpenStreetMap',
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    locate: 'My location',
    locateUnavailable: 'Location unavailable',
    dataAttribution: 'Charger data: EIPA (UDT), Open Charge Map, other sources',
    resetView: 'Frame the whole of Poland',
    resetViewShort: 'PL',
    preparing: 'Preparing the map…',
    loadFailed: 'Could not load the locations',
    announce: (visible, matching) => `${visible} of ${matching} matching locations in view`,
    ignoredFacets: (facets) => `Filters the map is ignoring: ${facets.join(', ')}`,
    points: (ac, dc) => `${ac} AC, ${dc} DC`,
    keyboardTitle: 'Keyboard navigation',
    keyboardHint: 'Arrow keys move through the list, Enter opens a location.',
    keyboardList: 'Locations in view',
    keyboardEmpty: 'No locations in view',
  },
  detail: {
    title: 'Location details',
    empty: 'Pick a point on the map to see its details',
    address: 'Address',
    operator: 'Operator',
    openingHours: 'Opening hours',
    alwaysOpen: 'Open 24/7',
    accessibility: 'Access',
    stations: 'Stations',
    points: 'Charge points',
    connectors: 'Connectors',
    power: 'Power',
    voltage: 'Voltage',
    cableAttached: 'Cable',
    cableAttachedYes: 'Tethered cable',
    cableAttachedNo: 'Socket, bring your own cable',
    price: 'Price',
    pricePerKwh: 'PLN / kWh',
    pricePerMinute: 'PLN / min',
    authMethods: 'Authentication',
    paymentMethods: 'Payment methods',
    lastUpdated: 'Updated',
    noPrice: 'The operator published no price',
    loadFailed: 'Could not load these details',
    loadFailedHint: 'Check your connection and try again.',
    notFound: 'That location was not found',
    closePanel: 'Close details',
    summary: (stations, points) =>
      `${stations} ${stations === 1 ? 'station' : 'stations'} · ${points} ${points === 1 ? 'point' : 'points'}`,
    stationHeading: (index, total) => `Station ${index} of ${total}`,
    pointCode: 'Point code',
    chargingModes: 'Charging mode',
    status: 'Status',
    statusAt: (when) => `As of ${when}`,
    statusCounts: (free, total) => `${free} of ${total} available`,
    noStatus: 'The operator reports no live status',
    distance: 'Distance',
    distanceAway: (formatted) => `${formatted} away`,
    showDistance: 'Show distance',
    locating: 'Finding your position…',
    geolocationDenied: 'Location access denied',
    geolocationFailed: 'Could not determine your position',
    directions: 'Get directions',
    directionsHint: 'Opens navigation in a new tab',
    openingHoursNone: 'The operator published no opening hours',
    connectorHeading: (index, total) => `Connector ${index} of ${total}`,
    noConnectors: 'No connectors in the registry',
    noPoints: 'No charge points in the registry',
    chargeCards: 'Your card pays here',
    chargeCardStart: 'Start in Telegram',
    chargeCardOpen: (card) => `Open ${card} in Telegram`,
  },
  unconfirmed: {
    badge: {
      'address-contested': 'Several stations here',
      'listing-retired': 'Reported as gone',
      unlisted: 'Not in the register',
    },
    heading: 'We could not confirm this station',
    reason: {
      'address-contested':
        'Several different stations are listed at this address, so we could not confirm which one this is.',
      'listing-retired':
        'Someone reported that a charger listed at this address is no longer there.',
      unlisted:
        'This station is not in the EIPA register - another source is the only thing that says it is here, and nothing corroborates it.',
    },
    addressContested: (listings) =>
      `${listings} different stations are listed at this address, so we could not confirm which one this is.`,
    question: 'Is this charger here?',
    yes: 'Yes, it is here',
    no: 'No, it is not',
    score: (score) => `Confirmations: ${score}`,
    toGo: (remaining) =>
      remaining === 1
        ? 'One more confirmation is still needed.'
        : `${remaining} more confirmations are still needed.`,
    daysToGo: (days) =>
      days === 1
        ? 'Answers from one more day are still needed.'
        : `Answers from ${days} more days are still needed.`,
    confirmed: 'Confirmed by drivers.',
    reportedGone: 'Drivers report that this charger is no longer there.',
    thanks: 'Thank you - your answer was recorded.',
    failed: 'Could not record your answer. Try again.',
    rateLimited: 'This browser has answered a lot of stations recently. Try again in an hour.',
    voting: 'Saving…',
  },
  voltage: {
    heading: 'Voltage',
    declared: 'Declared',
    derived: 'Derived',
    unknown: 'Unknown',
    declaredHint: 'A value published by an external database for this connector, not derived.',
    derivedHint: 'Derived from the charging mode and the connector standard. Open for the method.',
    unknownHint: 'No source publishes a voltage for this connector.',
    unknownExplanation:
      'EIPA publishes power in kW and nothing else. Where Open Charge Map has no matched record and neither the charging mode nor the connector standard settles it, the voltage stays unknown. No plausible-looking number is substituted.',
    methodology: 'Methodology',
    methodologyTitle: 'Where the voltage comes from',
    methodologyOpen: 'How voltage is derived',
    methodologyMissing: 'The methodology write-up is not in the repository yet.',
    methodologyMissingPath: 'Expected file: docs/voltage.md',
    nominal: 'Nominal voltage',
    range: 'Voltage range',
    atLeast: (formatted) => `from ${formatted}`,
    atMost: (formatted) => `up to ${formatted}`,
    voltUnit: 'V',
    ampUnit: 'A',
    amps: 'Current',
    phases: 'Phases',
    phasesValue: (count) => (count === 1 ? 'single phase' : `${count} phase`),
    confidenceLabel: 'Confidence',
    source: {
      'ge-declared': 'Other source',
      'ocm-declared': 'Open Charge Map',
      'derived-mode': 'Charging mode',
      'derived-connector': 'Connector standard',
    },
    sourceHint: {
      'ge-declared':
        'Another source published voltage and amperage for this connector. High confidence means it was matched by EVSE id, medium by the station address.',
      'ocm-declared': 'Open Charge Map published voltage and amperage for the matched connector.',
      'derived-mode': 'Derived from the IEC 61851 charging mode declared for the point.',
      'derived-connector':
        "Derived from the connector standard - the weakest evidence. Where DC hardware of this kind has been surveyed in Poland, its observed ceiling is used instead of the standard's.",
    },
    confidence: {
      high: 'high',
      medium: 'medium',
      low: 'low',
    },
    conflictTitle: 'The sources disagree',
    conflictShown: 'shown above',
    conflictHint:
      'We show whichever figure is tied most firmly to this particular connector, not whichever we think is right. Confidence describes the strength of that tie, not the accuracy of the number.',
    registrySource: 'EIPA (the registry)',
  },
  legend: {
    title: 'Legend',
    expand: 'Expand legend',
    collapse: 'Collapse legend',
    operators: 'Operators',
    power: 'Connector power',
    voltageClasses: 'Voltage classes',
    provenance: 'Voltage provenance',
    powerNote: 'A point on the map takes the colour of the strongest connector at the site.',
    voltageNote:
      'Voltage classes are a filter facet describing what the charger supports, not a car battery architecture. One CCS2 charger typically spans both DC classes. The actual range is shown on every connector.',
    brandRing: 'The ring around a dot is its operator.',
    cluster:
      'A number in a circle is a location count, and its colour is the band most of those locations fall in. Zoom in to break the group apart.',
  },
  powerBand: {
    slow: 'up to 11 kW',
    fast: '11-50 kW',
    rapid: '50-150 kW',
    ultra: 'above 150 kW',
  },
  powerBandShort: {
    slow: '< 11 kW',
    fast: '11-50',
    rapid: '50-150',
    ultra: '> 150',
  },
  footer: {
    data: 'Data',
    basemap: 'Basemap',
    about: 'About',
    methodology: 'Voltage methodology',
    updated: (when) => `data from ${when}`,
  },
  about: {
    title: 'About and licensing',
    open: 'About',
    intro:
      'This map joins EIPA - the official registry run by the Urząd Dozoru Technicznego - with electrical data from Open Charge Map. Below: where the data comes from, on what terms it is shown here, and how good it is in the current publish.',
    sourcesHeading: 'Sources and licences',
    licence: 'Licence',
    lastUpdated: 'Last updated',
    perFileTimestamps: 'Per-file EIPA timestamps',
    eipaName: 'EIPA / Urząd Dozoru Technicznego',
    eipaDescription:
      'Ewidencja Infrastruktury Paliw Alternatywnych - the official Polish registry of operators, stations and charge points.',
    eipaLicence: 'Public sector information, provided free of charge on UDT terms',
    ocmName: 'Open Charge Map',
    ocmDescription:
      'Voltage and amperage for matched connectors - the one electrical fact EIPA does not publish. Contributed by the Open Charge Map community.',
    ocmLicence: 'CC BY 4.0; Polish data provided under ODbL',
    otherName: 'Other sources',
    otherDescription:
      'Voltage and amperage for most Polish connectors, plus the DC ceilings of the hardware actually installed here. Community-contributed; the snapshot is refreshed by hand every few months.',
    otherLicence: 'Community data - terms are the publisher`s own',
    openFreeMapName: 'OpenFreeMap and OpenStreetMap',
    openFreeMapDescription:
      'Basemap tiles served by OpenFreeMap, built from OpenStreetMap data created by its contributors.',
    openFreeMapLicence: 'ODbL - © OpenStreetMap contributors',
    maplibreName: 'MapLibre GL JS',
    maplibreDescription: 'The engine that renders the map in the browser.',
    maplibreLicence: 'BSD-3-Clause',
    termsHeading: 'Conditions for reusing EIPA data',
    termsIntro:
      'The Urząd Dozoru Technicznego publishes conditions for the reuse of its public sector information in its Public Information Bulletin. Redistributing registry data is not forbidden, but it is conditional, and this site meets those conditions:',
    terms: [
      'The information carries a note of its source, giving the full name of the Urząd Dozoru Technicznego.',
      'The date the information was created or obtained is stated.',
      'A note is included that the information was processed in the course of reuse.',
      'Fragments of the obtained content are presented as quotations, with a footnote naming the source.',
      'A notice is included that the Urząd Dozoru Technicznego bears no responsibility for the content of information produced by that processing.',
    ],
    termsCompliance:
      'Processing note: the EIPA data shown on this map has been processed in the course of reuse. Registry records are matched against Open Charge Map points, and connector voltage is derived where no source declares it.',
    termsAccess:
      'Access to EIPA data requires registration, is free of charge, and is subject to an hourly download limit. The registry manual expressly permits presenting the downloaded data on your own website.',
    disclaimer:
      'The Urząd Dozoru Technicznego bears no responsibility for the currency of the data submitted to the registry by infrastructure operators, nor for the content of information produced by processing it.',
    qualityHeading: 'Data quality',
    qualityIntro:
      'The numbers below are read from the current publish’s meta.json - none of them are hardcoded.',
    countsHeading: 'What this publish covers',
    voltageHeading: 'Voltage coverage',
    matchHeading: 'Open Charge Map matching',
    availabilityHeading: 'Point availability',
    sites: 'Locations',
    stations: 'Stations',
    points: 'Charge points',
    connectors: 'Connectors',
    operatorCount: 'Operators',
    declared: 'Declared by Open Charge Map',
    derived: 'Derived',
    unknownVoltage: 'Unknown',
    ocmCandidates: 'EIPA locations considered',
    ocmMatched: 'Matched to an Open Charge Map record',
    ocmUnmatched: 'Left unmatched',
    ocmMatchRate: 'Match rate',
    generated: 'Publish generated',
    pipelineVersion: 'Pipeline version',
    loadFailed: 'Could not load meta.json',
    share: (part, total) => (total === 0 ? '-' : `${Math.round((part / total) * 100)}%`),
  },
  eipaSource: {
    dictionary: 'Dictionaries',
    operator: 'Operators',
    pool: 'Locations',
    station: 'Stations',
    point: 'Charge points',
    dynamic: 'Live data',
  },
  weekday: {
    1: 'Monday',
    2: 'Tuesday',
    3: 'Wednesday',
    4: 'Thursday',
    5: 'Friday',
    6: 'Saturday',
    7: 'Sunday',
  },
  authMethod: {
    unrestricted: 'Unrestricted',
    'no-access': 'No access',
    'rfid-mifare-classic': 'RFID Mifare Classic',
    'rfid-mifare-desfire': 'RFID Mifare DESFire',
    'rfid-calypso': 'RFID Calypso',
    pinpad: 'PIN pad',
    app: 'Mobile app',
    'phone-rfid': 'Phone with RFID',
    'iso15118-plc': 'ISO 15118 (PLC)',
    'iso15118-wireless': 'ISO 15118 (wireless)',
    'phone-voice': 'Phone call',
    'phone-sms': 'SMS',
    'prepaid-card': 'Prepaid card',
    other: 'Other',
  },
  stationType: {
    electric: 'Electric',
    gas: 'CNG / LNG',
    hydrogen: 'Hydrogen',
  },
  currentType: {
    AC: 'Alternating current (AC)',
    DC: 'Direct current (DC)',
    unknown: 'Unknown',
  },
  availability: {
    free: 'Available',
    occupied: 'In use',
    unavailable: 'Out of service',
    unknown: 'No data',
  },
  voltageClass: {
    'ac-230': 'AC 230 V',
    'ac-400': 'AC 400 V',
    'dc-400': 'DC up to 500 V',
    'dc-800': 'DC above 500 V',
    unknown: 'Unknown',
  },
  connector: {
    DOMESTIC: 'Domestic socket',
    IEC_60309_1P: 'IEC 60309 single phase',
    IEC_60309_3P: 'IEC 60309 three phase',
    IEC_60309_DC: 'IEC 60309 DC',
    TYPE1: 'Type 1 (J1772)',
    TYPE2: 'Type 2 (Mennekes)',
    TYPE3A: 'Type 3A (SCAME)',
    TYPE3C: 'Type 3C',
    CCS1: 'CCS Combo 1',
    CCS2: 'CCS Combo 2',
    CHADEMO: 'CHAdeMO',
    TESLA: 'Tesla',
    GBT_AC: 'GB/T AC',
    GBT_DC: 'GB/T DC',
    INDUCTIVE_LARGE: 'Inductive (large paddle)',
    INDUCTIVE_SMALL: 'Inductive (small paddle)',
    AVCON: 'Avcon',
    MARECHAL: 'Marechal',
    BETTER_PLACE: 'Better Place',
    UNKNOWN: 'Unknown connector',
  },
  paymentMethod: {
    unspecified: 'Unspecified',
    free: 'Free',
    'operator-contract': 'Operator contract',
    'payment-card': 'Payment card',
    cash: 'Cash',
    'prepaid-card': 'Prepaid card',
    'fleet-card': 'Fleet card',
    'bank-transfer': 'Bank transfer',
    'online-payment': 'Online payment',
    other: 'Other',
  },
}

export const STRINGS: Record<Language, Strings> = { pl, en }

export const getStrings = (language: Language = DEFAULT_LANGUAGE): Strings => STRINGS[language]

export const isLanguage = (value: string): value is Language =>
  (LANGUAGES as readonly string[]).includes(value)
